import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

interface Company {
  id: number | string;
  company_name: string;
  address?: string;
  pancard?: string;
  city?: string;
  state?: string;
  country?: string;
  gst_number?: string;
  gst_uin?: string;
  code?: string;
  cin?: string;
  email?: string;
  [key: string]: any;
}

interface Customer {
  id: number | string;
  company_name: string;
  city?: string;
  state?: string;
  gst_number?: string;
  gst_uin?: string;
  [key: string]: any;
}

interface Setting {
  IGST: number;
  CGST: number;
  SGST: number;
  InvoiceSN?: string;
}

interface InvoiceRow {
  particulars: string;
  hsn_sac: string;
  amount: number;
}

const initialInvoiceRow: InvoiceRow = {
  particulars: "",
  hsn_sac: "",
  amount: 0,
};

const InvoiceCreator: React.FC = () => {
  // Dropdown/source states
  const [companies, setCompanies] = useState<Company[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [settings, setSettings] = useState<Setting | null>(null);
  const [showInvoice, setShowInvoice] = useState(false);

  // Form states
  const [companyId, setCompanyId] = useState<string | number>("");
  const [customerId, setCustomerId] = useState<string | number>("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [rows, setRows] = useState<InvoiceRow[]>([{ ...initialInvoiceRow }]);
  const [sgstAmount, setSgstAmount] = useState<number>(0);
  const [cgstAmount, setCgstAmount] = useState<number>(0);
  const [igstAmount, setIgstAmount] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);

  // Other states
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Hold invoice data separately for displaying in popup after saving to API
  const [invoicePreviewData, setInvoicePreviewData] = useState<{
    companyId: string | number;
    customerId: string | number;
    invoiceNo: string;
    invoiceDate: string;
    rows: InvoiceRow[];
    sgstAmount: number;
    cgstAmount: number;
    igstAmount: number;
    totalAmount: number;
  } | null>(null);

  // Always select the actual company and customer object (not just by id every time)
  const selectedCompany = companies.find(
    (c) => String(c.id) === String(companyId)
  );
  const selectedCustomer = customers.find(
    (c) => String(c.id) === String(customerId)
  );

  // Helper to calculate taxes and total
  const calculateTaxesAndTotal = useCallback(
    (
      _rows = rows,
      _companyId = companyId,
      _customerId = customerId,
      _settings = settings
    ) => {
      if (
        !_companyId ||
        !_customerId ||
        !_settings ||
        companies.length === 0 ||
        customers.length === 0
      ) {
        setSgstAmount(0);
        setCgstAmount(0);
        setIgstAmount(0);
        setTotalAmount(0);
        return;
      }

      const company = companies.find(
        (c) => String(c.id) === String(_companyId)
      );
      const customer = customers.find(
        (c) => String(c.id) === String(_customerId)
      );

      if (!company || !customer) {
        setSgstAmount(0);
        setCgstAmount(0);
        setIgstAmount(0);
        setTotalAmount(0);
        return;
      }

      const sum = _rows.reduce((a, b) => a + (Number(b.amount) || 0), 0);

      let _sgst = 0,
        _cgst = 0,
        _igst = 0;

      // If states match, SGST + CGST. If different, IGST.
      if (
        company.state &&
        customer.state &&
        company.state.trim().toLowerCase() ===
          customer.state.trim().toLowerCase()
      ) {
        _sgst = ((_settings.SGST || 0) * sum) / 100;
        _cgst = ((_settings.CGST || 0) * sum) / 100;
        _igst = 0;
      } else {
        _sgst = 0;
        _cgst = 0;
        _igst = ((_settings.IGST || 0) * sum) / 100;
      }

      setSgstAmount(Number(_sgst.toFixed(2)));
      setCgstAmount(Number(_cgst.toFixed(2)));
      setIgstAmount(Number(_igst.toFixed(2)));
      setTotalAmount(parseFloat((sum + _sgst + _cgst + _igst).toFixed(2)));
    },
    [companies, customers, settings, companyId, customerId, rows]
  );

  // Fetch companies
  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_URL}/auth/companies`)
      .then((res) => setCompanies(res.data))
      .catch(() => setErrorMsg("Could not fetch companies."))
      .finally(() => setLoading(false));
  }, []);

  // Fetch customers
  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_URL}/auth/customers`)
      .then((res) => setCustomers(res.data))
      .catch(() => setErrorMsg("Could not fetch buyers/customers."))
      .finally(() => setLoading(false));
  }, []);

  // Fetch settings
  useEffect(() => {
    axios
      .get(`${API_URL}/auth/setting`)
      .then((res) => setSettings(res.data))
      .catch(() => setErrorMsg("Could not fetch tax settings."));
  }, []);

  // Update Invoice Number on auto-setting
  useEffect(() => {
    if (settings && settings.InvoiceSN) setInvoiceNo(settings.InvoiceSN);
  }, [settings]);

  // Calculate amount/taxes/total whenever rows, company, or customer changes
  useEffect(() => {
    calculateTaxesAndTotal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, companyId, customerId, settings, companies, customers]);

  // Handlers
  const onRowChange = (i: number, name: string, value: string | number) => {
    const newRows = [...rows];
    // sanitize number
    if (name === "amount") newRows[i][name] = Number(value);
    else {
      if (name === "particulars" || name === "hsn_sac") {
        (newRows[i] as any)[name] = value as string;
      }
    }
    setRows(newRows);
    // After any input row change, recalculate
    setTimeout(() => {
      calculateTaxesAndTotal(newRows, companyId, customerId, settings);
    }, 0);
  };

  const onCompanyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCompanyId(e.target.value);
    setTimeout(() => {
      calculateTaxesAndTotal(rows, e.target.value, customerId, settings);
    }, 0);
  };

  const onCustomerChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setCustomerId(e.target.value);
    setTimeout(() => {
      calculateTaxesAndTotal(rows, companyId, e.target.value, settings);
    }, 0);
  };

  const addRow = () => {
    const updatedRows = [...rows, { ...initialInvoiceRow }];
    setRows(updatedRows);
    setTimeout(() => {
      calculateTaxesAndTotal(updatedRows, companyId, customerId, settings);
    }, 0);
  };

  const removeRow = (i: number) => {
    if (rows.length === 1) return;
    const rr = [...rows];
    rr.splice(i, 1);
    setRows(rr);
    setTimeout(() => {
      calculateTaxesAndTotal(rr, companyId, customerId, settings);
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg("");
    setErrorMsg("");
    // Save all current values for preview
    setInvoicePreviewData({
      companyId,
      customerId,
      invoiceNo,
      invoiceDate,
      rows: JSON.parse(JSON.stringify(rows)),
      sgstAmount,
      cgstAmount,
      igstAmount,
      totalAmount,
    });
    setShowInvoice(true); // OPEN POPUP

    // Don't reset data here - only if API call succeeds
    // force recalculation before submission if any late change
    calculateTaxesAndTotal(rows, companyId, customerId, settings);
    if (!companyId || !customerId || !invoiceNo || !invoiceDate || totalAmount <= 0) {
      setErrorMsg("Please fill all required fields and at least one row with amount.");
      return;
    }
    setSubmitLoading(true);
    try {
      const particularsArr = rows.map((r) => r.particulars).join(", ");
      const hsnArr = rows.map((r) => r.hsn_sac).join(", ");

      const payload = {
        Company_id: companyId,
        Customer_id: customerId,
        SGSTAmount: sgstAmount,
        CGSTAmount: cgstAmount,
        IGSTAmount: igstAmount,
        InvoiceNo: invoiceNo,
        Date: invoiceDate,
        Particulars: particularsArr,
        HSN_SAC: hsnArr,
        Total_Amount: totalAmount,
      };

      await axios.post(`${API_URL}/auth/invoice`, payload);

      setSuccessMsg("Invoice generated and saved successfully.");
      // Reset only after success!
      setRows([{ ...initialInvoiceRow }]);
      setInvoiceNo(settings?.InvoiceSN || "");
      setInvoiceDate("");
      setCompanyId("");
      setCustomerId("");
      setSgstAmount(0);
      setCgstAmount(0);
      setIgstAmount(0);
      setTotalAmount(0);
    } catch (err: any) {
      setErrorMsg(
        err?.response?.data?.error || "Failed to generate the invoice."
      );
    } finally {
      setSubmitLoading(false);
    }
  };
  const handlePrint = () => {
    const printContents = document.getElementById("invoice-print")?.innerHTML;
    const win = window.open("", "", "width=900,height=700");

    if (win && printContents) {
      win.document.write(`
        <html>
          <head>
            <title>Invoice</title>
            <style>
              body{font-family:Arial;padding:20px}
              table{width:100%;border-collapse:collapse}
              td,th{border:1px solid #000;padding:6px;text-align:left}
            </style>
          </head>
          <body>${printContents}</body>
        </html>
      `);
      win.document.close();
      win.print();
    }
  };
  return (
    <div className="max-w-4xl mx-auto py-10 px-4">
      <h2 className="text-2xl font-bold mb-4 text-blue-900">Create Invoice</h2>
      {errorMsg && (
        <div className="mb-4 p-2 bg-red-100 text-red-700 rounded border border-red-200">
          {errorMsg}
        </div>
      )}
      {successMsg && (
        <div className="mb-4 p-2 bg-green-100 text-green-700 rounded border border-green-200">
          {successMsg}
        </div>
      )}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg shadow-lg p-6 space-y-6"
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="font-medium text-gray-700 block mb-1">
              Select Company <span className="text-red-600">*</span>
            </label>
            <select
              value={companyId}
              onChange={onCompanyChange}
              required
              className="form-select block w-full border-gray-300 rounded-md shadow-sm"
            >
              <option value="">-- Select --</option>
              {companies.map((c) => (
                <option value={c.id} key={c.id}>
                  {c.company_name}
                  {c.gst_number ? ` (GST: ${c.gst_number})` : ""}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-medium text-gray-700 block mb-1">
              Select Buyer <span className="text-red-600">*</span>
            </label>
            <select
              value={customerId}
              onChange={onCustomerChange}
              required
              className="form-select block w-full border-gray-300 rounded-md shadow-sm"
            >
              <option value="">-- Select --</option>
              {customers.map((c) => (
                <option value={c.id} key={c.id}>
                  {c.company_name}
                  {c.gst_number ? ` (GST: ${c.gst_number})` : ""}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="font-medium text-gray-700 block mb-1">
              Invoice No. <span className="text-red-600">*</span>
            </label>
            <input
              type="text"
              className="form-input block w-full border-gray-300 rounded-md"
              value={invoiceNo}
              onChange={(e) => setInvoiceNo(e.target.value)}
              required
              placeholder="Invoice No."
            />
          </div>
          <div>
            <label className="font-medium text-gray-700 block mb-1">
              Invoice Date <span className="text-red-600">*</span>
            </label>
            <input
              type="date"
              className="form-input block w-full border-gray-300 rounded-md"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              required
            />
          </div>
        </div>

        {/* Invoice Table Rows */}
        <div>
          <label className="font-medium text-gray-700 block mb-1">
            Particulars & Amount <span className="text-red-600">*</span>
          </label>
          <table className="min-w-full bg-gray-50 rounded">
            <thead>
              <tr>
                <th className="p-2">Particulars</th>
                <th className="p-2">HSN/SAC</th>
                <th className="p-2">Amount</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  <td className="p-2">
                    <input
                      type="text"
                      className="form-input w-full"
                      value={row.particulars}
                      onChange={(e) =>
                        onRowChange(i, "particulars", e.target.value)
                      }
                      required
                    />
                  </td>
                  <td className="p-2">
                    <input
                      type="text"
                      className="form-input w-full"
                      value={row.hsn_sac}
                      onChange={(e) =>
                        onRowChange(i, "hsn_sac", e.target.value)
                      }
                      required
                    />
                  </td>
                  <td className="p-2">
                    <input
                      type="number"
                      className="form-input w-full"
                      value={row.amount}
                      min="0"
                      onChange={(e) =>
                        onRowChange(i, "amount", e.target.value)
                      }
                      required
                    />
                  </td>
                  <td className="p-2 text-center">
                    {rows.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeRow(i)}
                        className="text-red-600 font-bold px-2 py-1 rounded hover:bg-red-100"
                        title="Remove row"
                        tabIndex={-1}
                      >
                        &times;
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button
            type="button"
            onClick={addRow}
            className="mt-2 px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 transition text-sm"
          >
            + Add More
          </button>
        </div>

        {/* Tax & total preview */}
        {settings && (
          <div className="grid md:grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
            <div>
              <div className="mb-2 flex justify-between">
                <span>SGST ({settings.SGST}%)</span>
                <span>₹ {sgstAmount.toFixed(2)}</span>
              </div>
              <div className="mb-2 flex justify-between">
                <span>CGST ({settings.CGST}%)</span>
                <span>₹ {cgstAmount.toFixed(2)}</span>
              </div>
              <div className="mb-2 flex justify-between">
                <span>IGST ({settings.IGST}%)</span>
                <span>₹ {igstAmount.toFixed(2)}</span>
              </div>
              <div className="border-t pt-3 font-bold flex justify-between text-lg">
                <span>Total</span>
                <span>₹ {totalAmount.toLocaleString("en-IN", {minimumFractionDigits: 2})}</span>
              </div>
            </div>
            <div className="flex flex-col justify-center items-end text-xs text-gray-400">
              <span>
                <span className="font-semibold">* Tax type</span> is auto-chosen depending on state: same = SGST/CGST, different = IGST.
              </span>
            </div>
          </div>
        )}

        {/* Submit & Generate */}
        <div className="flex items-center gap-4 pt-2">
          <button
            type="submit"
            className={`px-5 py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 transition text-lg ${
              submitLoading ? "opacity-70 cursor-not-allowed" : ""
            }`}
            disabled={submitLoading}
          >
            {submitLoading ? "Generating..." : "Generate Invoice"}
          </button>
          {/* Placeholders for PDF/HTML after generation */}
        </div>
      </form>
      {showInvoice && invoicePreviewData && (
        <>
          {/* Debugger statement for development purposes */}
          {/* {(() => { debugger; return null; })()} */}

          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white w-[900px] max-h-[90vh] overflow-auto rounded shadow-lg p-6">
              {/* Buttons */}
              <div className="flex justify-end gap-3 mb-4">
                <button
                  onClick={handlePrint}
                  className="bg-blue-600 text-white px-4 py-2 rounded"
                >
                  Print
                </button>
                <button
                  onClick={() => setShowInvoice(false)}
                  className="bg-gray-500 text-white px-4 py-2 rounded"
                >
                  Close
                </button>
              </div>

              {/* INVOICE */}
              <div id="invoice-print" className="p-4 text-sm">
                <table className="w-full border border-black border-collapse">
                  {/* HEADER */}
                  <tr>
                    <td className="border p-2 w-2/3">
                      <b>{companies.find(c=>String(c.id)===String(invoicePreviewData.companyId))?.company_name}</b><br/>
                      {companies.find(c=>String(c.id)===String(invoicePreviewData.companyId))?.address}<br/>
                      GSTIN: {companies.find(c=>String(c.id)===String(invoicePreviewData.companyId))?.gst_number}<br/>
                      State: {companies.find(c=>String(c.id)===String(invoicePreviewData.companyId))?.state}<br/>
                      CIN: {companies.find(c=>String(c.id)===String(invoicePreviewData.companyId))?.cin}<br/>
                      Email: {companies.find(c=>String(c.id)===String(invoicePreviewData.companyId))?.email}
                    </td>
                    <td className="border p-2">
                      <b>Invoice No:</b> {invoicePreviewData.invoiceNo}<br/>
                      <b>Dated:</b> {invoicePreviewData.invoiceDate}
                    </td>
                  </tr>

                  {/* BUYER */}
                  <tr>
                    <td className="border p-2">
                      <b>Buyer (Bill to)</b><br/><br/>
                      <b>{customers.find(c=>String(c.id)===String(invoicePreviewData.customerId))?.company_name}</b><br/>
                      City: {customers.find(c=>String(c.id)===String(invoicePreviewData.customerId))?.city}<br/>
                      State: {customers.find(c=>String(c.id)===String(invoicePreviewData.customerId))?.state}<br/>
                      GSTIN: {customers.find(c=>String(c.id)===String(invoicePreviewData.customerId))?.gst_number}
                    </td>
                    <td className="border p-2"></td>
                  </tr>
                </table>

                {/* ITEMS */}
                <table className="w-full border border-black border-collapse mt-2">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border p-2">Particulars</th>
                      <th className="border p-2">HSN/SAC</th>
                      <th className="border p-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoicePreviewData.rows.map((r,i)=>(
                      <tr key={i}>
                        <td className="border p-2">{r.particulars}</td>
                        <td className="border p-2">{r.hsn_sac}</td>
                        <td className="border p-2 text-right">₹ {r.amount}</td>
                      </tr>
                    ))}

                    <tr>
                      <td className="border p-2 text-right" colSpan={2}>
                        CGST
                      </td>
                      <td className="border p-2 text-right">
                        ₹ {invoicePreviewData.cgstAmount}
                      </td>
                    </tr>

                    <tr>
                      <td className="border p-2 text-right" colSpan={2}>
                        SGST
                      </td>
                      <td className="border p-2 text-right">
                        ₹ {invoicePreviewData.sgstAmount}
                      </td>
                    </tr>

                    {invoicePreviewData.igstAmount > 0 && (
                      <tr>
                        <td className="border p-2 text-right" colSpan={2}>
                          IGST
                        </td>
                        <td className="border p-2 text-right">
                          ₹ {invoicePreviewData.igstAmount}
                        </td>
                      </tr>
                    )}

                    <tr className="font-bold">
                      <td className="border p-2 text-right" colSpan={2}>
                        Total
                      </td>
                      <td className="border p-2 text-right">
                        ₹ {invoicePreviewData.totalAmount}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* BANK DETAILS */}
                <table className="w-full border border-black border-collapse mt-2">
                  <tr>
                    <td className="border p-2 w-1/2">
                      <b>Company's Bank Details</b><br/>
                      A/c Holder Name : {companies.find(c=>c.id==invoicePreviewData.companyId)?.company_name}<br/>
                      Bank Name : IDFC First Bank<br/>
                      A/c No : 10121994987<br/>
                      Branch : New Sanganer Road
                    </td>
                    <td className="border p-2 text-right">
                      For {companies.find(c=>c.id==invoicePreviewData.companyId)?.company_name}
                      <br/><br/><br/>
                      Authorised Signatory
                    </td>
                  </tr>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default InvoiceCreator;